import React from "react";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import { WrappedContentPlaceholder } from "./contentPlaceholder";
import styles from "./watchlistWrapped.module.css";
import Notification from "~components/Notification";
import { WrappedDailySnapshot } from "~components/WrappedDailySnapshot";
import { WrappedTopMovers } from "~components/WrappedTopMovers";
import { WrappedPerformanceChart } from "~components/WrappedPerformanceChart";
import { LOADING_STATE } from "~constants/Portfolio";
import { WrappedWatchlist } from "~types/octagon";
import { PlaceholderWithError } from "~components/PlaceholderWithError";

interface Props {
  loadingState: string;
  wrappedWatchlist?: WrappedWatchlist;
  onRetry: () => void;
}

const WATCHLIST_URL = `/markets/watchlist`;
const CHART_WITH_SHARES_CAPTION = "Performance weighted by your shares";

function NoTickersView() {
  const handleGoToWatchlist = () => {
    window.location.href = WATCHLIST_URL;
  };

  return (
    <div className={styles.noTickersViewContainer}>
      <Notification
        className={styles.noTickersNotification}
        text="Add tickers to your watchlist to see your assets’ daily performance."
        variant="lineBreak"
        title=""
        actionText="Go to Watchlist"
        onActionClick={handleGoToWatchlist}
      />
    </div>
  );
}

export function WrappedContentSection({
  loadingState,
  wrappedWatchlist,
  onRetry,
}: Props) {
  if (loadingState === LOADING_STATE.LOADING) {
    return <WrappedContentPlaceholder />;
  }

  if (loadingState === LOADING_STATE.FAILED) {
    return (
      <PlaceholderWithError
        errorMessage={"There was an error loading watchlist wrapped data."}
        placeholder={<WrappedContentPlaceholder />}
        onRetryClick={onRetry}
      />
    );
  }

  if (loadingState === LOADING_STATE.EMPTY) {
    return <div>No wrapped watchlist data available.</div>;
  }

  if (loadingState === LOADING_STATE.COMPLETE) {
    const hasLots =
      Boolean(wrappedWatchlist?.totalValue?.value) &&
      Boolean(wrappedWatchlist?.totalCost?.value);

    if (hasLots) {
      // Fires when a user has lots associated with their watchlist that power
      // the snapshot data (gain/loss, etc) so those values should be nonzero
      trackClickEvent(
        "watchlistPresence",
        "user_with_lots",
        "watchlist",
        "uiaction"
      );
    }

    const hasTickers = (wrappedWatchlist?.topMovers.length ?? 0) > 0;

    if (!hasTickers) {
      return <NoTickersView />;
    }

    if (wrappedWatchlist) {
      return (
        <div className={styles.mainContent}>
          <div className={styles.snapshotAndMoversSection}>
            <div className={styles.snapshotWrapper}>
              <WrappedDailySnapshot
                todayGainLoss={wrappedWatchlist.todayGainLoss}
                totalGainLoss={wrappedWatchlist.totalGainLoss}
                totalValue={wrappedWatchlist.totalValue}
                totalCost={wrappedWatchlist.totalCost}
              />
            </div>

            <div className={styles.verticalDivider} role="separator" />

            <div className={styles.moversSection}>
              <WrappedTopMovers
                movers={wrappedWatchlist.topMovers}
                currency={wrappedWatchlist.currency}
                hasLots={hasLots}
              />
            </div>
          </div>

          <div className={styles.horizontalDivider} role="separator" />

          <div className={styles.chartSection}>
            <WrappedPerformanceChart
              chartData={wrappedWatchlist.chartData}
              chartCaption={hasLots ? CHART_WITH_SHARES_CAPTION : ""}
            />
          </div>
        </div>
      );
    }
  }

  return null;
}
